import translate from "@deosite/helpers/langLibrary";
import React, { useRef, useEffect, useMemo, useState } from "react";
import useWindowSize from "@commerce-ui/core2/useWindowSize";
import { default as themeDajar } from "../theme";

import PageContainer from "../containers/UI/PageContainer";

import MetaTags from "../components/MetaTags";
import { META_TYPE } from "@deosite/components-new/MetaTags";
import ProductHead from "../components/ProductHead/index";
import CardsSlider from "../components/CardsSlider";
import ProductBox from "../components/ProductBox";
import Container from "@commerce-ui/core2/Container";
import Box from "@commerce-ui/core2/Box";
import Grid from "@commerce-ui/core2/Grid";
import CardWithLabel from "../components/CardWithLabel";

import SectionWithConfigurableHeader from "@deosite/sections/SectionWithConfigurableHeader";

import getSkusFromProductLinks from "@deosite/helpers/getSkusFromProductLinks";
import { removeSpecificCharactersFromString } from "@deosite/helpers/handleLanguageCharacters";
import { readCacheFile, saveCacheFile } from "@deosite/helpers/cache";
import getDefaultProductImage from "@deosite/helpers/getDefaultProductImage";

import { RichText } from "prismic-reactjs";
import BrandInfo from "@deosite/components-new/BrandInfo";

import {
  fetcher as productFetcher,
  useProduct
} from "@deosite/api/hooks/REST/useProduct";

import {
  fetcher as productsFromCollectionFetcher,
  useProductsFromCollection
} from "@deosite/api/hooks/REST/useProductsFromCollection";

import {
  fetcher as productsBySkusFetcher,
  useProductsBySkus
} from "@deosite/api/hooks/REST/useProductsBySkus";

import {
  useCategory,
  fetcher as categoryFetcher
} from "@deosite/api/hooks/REST/useCategory";

import {
  fetcher as prismicBrandInfoFetcher,
  usePrismicBrandInfo
} from "@deosite/api/hooks/prismic/usePrismicBrandInfo";

import {
  fetcher as prismicCollectionInfoFetcher,
  usePrismicCollectionInfo
} from "@deosite/api/hooks/prismic/usePrismicCollectionInfo";

import { useCategoryTree } from "@deosite/api/hooks/common/useCategoryTree";
import useAnalyticsAndCommerceTags from "@deosite/api/hooks/common/useAnalytics4AndCommerceTags";
import useSalesmanago from "@deosite/api/hooks/REST/useSalesmanago";
import { useProductHeadOptions } from "@deosite/api/hooks/common/useProductHeadOptions";
import { useLanguage } from "@deosite/api/hooks/common/LanguageProvider";

import rootCategories from "../../conf/storeRootCategory.json";
import brandCategories from "../../conf/storeBrandCategory.json";

import isGlobalEnvEnabled from "@deosite/helpers/isGlobalEnvEnabled";
import { useHit } from "@deosite/components-new/Hit/provider/HitProvider";
import { useLoader } from "@deosite/api/hooks/common/useLoader";

import ErrorPage from "../pages/_error";

const Page = ({
  product: initialProduct,
  upSellSkus,
  productsFromCollection: initialProductsFromCollection,
  sku,
  productUrl,
  productsCompleteTheLook: initialProductsCompleteTheLook,
  footerContent,
  initialCategory,
  brandInfo: initialBrandInfo,
  collectionInfo: initialCollectionInfo,
  isProductPageHit
}) => {
  const { language } = useLanguage();
  const { sale } = useHit(!isProductPageHit);

  const currentSku = sale?.sku ?? sku;

  const { externalContentTimeout } = useLoader();

  let { data: productData } = useProduct(
    currentSku,
    productUrl,
    language,
    initialProduct
  );

  if (isProductPageHit && productData?.product?.otherVariants?.length > 0) {
    productData.product.otherVariants = [];
  }

  const { data: brandInfo } = usePrismicBrandInfo(
    productData?.product.producerId,
    language,
    initialBrandInfo
  );

  const { data: collectionInfo } = usePrismicCollectionInfo(
    productData?.product?.collection.id,
    language,
    initialCollectionInfo
  );

  if (productData?.product && !upSellSkus) {
    upSellSkus = getSkusFromProductLinks(productData.product.relatedProducts);
  }

  const { data: productsCompleteTheLook } = useProductsBySkus(
    upSellSkus,
    12,
    initialProductsCompleteTheLook
  );

  const { data: productsFromCollection } = useProductsFromCollection(
    productData?.product?.collection?.id,
    12,
    initialProductsFromCollection
  );

  const categoryId =
    productData?.product?.category &&
    productData?.product?.category?.id !== 2 &&
    productData?.product?.category?.id;

  const { data: category } = useCategory(categoryId, initialCategory);

  const collectionName =
    productData?.product?.collection?.name &&
    removeSpecificCharactersFromString({
      string: productData.product.collection.name
        .toLowerCase()
        .replace(/\s/g, "_")
        .replace(/\//g, "_")
        .replace(/&/g, "_")
        .replace(/(_)\1+/g, "_")
    });

  const categoryUrl = category
    ? category.url
    : rootCategories[language.language];

  const productCategory = category
    ? {
        name: category?.name,
        url: categoryUrl
      }
    : null;

  const imagesWithDefaultImage = getDefaultProductImage(
    global.__appConfig__.pdpDefaultImage,
    language?.language,
    productData
  );

  if (imagesWithDefaultImage) {
    productData.product.images = imagesWithDefaultImage;
  }

  const translations = {
    seeAll: translate("see all"),
    popularCategories: translate("Popular categories"),
    newIn: translate("New in"),
    allNew: translate("all new"),
    bestsellers: translate("Bestsellers"),
    seoDescription: translate("Default seo description"),
    seoTitle: translate("Default seo title"),
    fromCollection: translate("Also from this collection"),
    completeTheLook: translate("Complete the look"),
    shopAllCollection: translate("Shop all{collectionName}}", {
      collectionName: productData?.product.collection.name
    })
  };

  const product = productData?.product;

  const categoryIdOutlet = isGlobalEnvEnabled({
    globalEnv: global.__appConfig__.categoryOutletId,
    language,
    returnValue: true
  });

  const { getCategoryNameForGivenCategoryId } = useCategoryTree();
  const { productVisited } = useAnalyticsAndCommerceTags();
  const handleSalesmanagoEvent = useSalesmanago();

  const producerForUrl = productData?.product?.producer?.toLowerCase();

  const collectionUrl = producerForUrl
    ? `${
        rootCategories[language.language]
      }/${removeSpecificCharactersFromString({
        string: producerForUrl,
        removeSpaces: true
      })}-${collectionName}`
    : `${
        rootCategories[language.language]
      }/${removeSpecificCharactersFromString({
        string: productData?.product?.collection?.name,
        removeSpaces: true,
        spaceChar: "_"
      }).toLowerCase()}`;

  const firstUpdate = useRef(true);
  const size = useWindowSize();
  const [isMobile, setMobile] = useState(null);

  useEffect(() => {
    const isMobileSize = size.width <= parseInt(themeDajar().breakpoints.md);
    if (isMobile !== isMobileSize) {
      setMobile(isMobileSize);
    }
  }, [size.width]);

  useEffect(() => {
    if (firstUpdate.current === categoryId) {
      return;
    }
    firstUpdate.current = categoryId;
    productData?.product &&
      productVisited(
        productData.product,
        getCategoryNameForGivenCategoryId(categoryId)
      );
    handleSalesmanagoEvent("OFFER", {
      products: [productData?.product.sku],
      value:
        productData?.product.price.special ?? productData?.product.price.regular
    });
  });

  const { activeProduct } = useProductHeadOptions();

  const currentProduct = activeProduct ? activeProduct : product;
  const dataRequiredToHideLoader = useMemo(
    () => [productData, productsCompleteTheLook, productsFromCollection],
    [productData, productsCompleteTheLook, productsFromCollection]
  );

  if (productData?.error) {
    return <ErrorPage statusCode={404} />;
  }

  return (
    <>
      <MetaTags
        page={META_TYPE.PRODUCT_PAGE}
        title={currentProduct?.name}
        description={
          currentProduct?.info?.description
            ? product?.info?.description
            : "Professional product"
        }
        imageUrl={product && product.image && product.image.originalSrc}
        url={product && product.name && product.url}
        sku={product?.sku}
        productRegularPrice={product?.price?.regular}
        productSpecialPrice={
          product?.price?.special
            ? product?.price?.special
            : product?.price?.regular
        }
        productAvailability={product?.availability?.stock}
        productBrand={product?.producer}
        productCategory={
          product
            ? getCategoryNameForGivenCategoryId(product?.category?.id)
            : null
        }
      />
      <PageContainer
        footerContent={footerContent}
        key={productData?.product?.sku}
        dataRequiredToHideLoader={dataRequiredToHideLoader}
      >
        {productData?.product && (
          <ProductHead
            product={productData?.product}
            productCategory={productCategory}
            productFromOutlet={
              categoryIdOutlet &&
              productData.product.category.pathIds?.includes(categoryIdOutlet)
            }
            isMobile={isMobile}
            rootCategories={rootCategories}
            brandCategories={brandCategories}
            isProductPageHit={isProductPageHit}
          />
        )}
        {productsFromCollection?.length > 0 && productData?.product && (
          <SectionWithConfigurableHeader
            title={translations.fromCollection}
            link={{
              url: collectionUrl,
              name: translations.shopAllCollection
            }}
            noSpaceAfterHeader={true}
          >
            <CardsSlider>
              {productsFromCollection.map((product, index) => {
                if (product.producerId === productData.product.producerId)
                  return (
                    <ProductBox key={index} product={product} colors={[]} />
                  );
              })}
            </CardsSlider>
          </SectionWithConfigurableHeader>
        )}
        {productsCompleteTheLook?.length > 0 && (
          <SectionWithConfigurableHeader title={translations.completeTheLook}>
            <CardsSlider>
              {productsCompleteTheLook.map((product, index) => (
                <ProductBox key={index} product={product} colors={[]} />
              ))}
            </CardsSlider>
          </SectionWithConfigurableHeader>
        )}

        {brandInfo && !externalContentTimeout && (
          <SectionWithConfigurableHeader>
            <Container
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center"
              }}
            >
              <Box sx={{ width: ["100%", null, "50%"] }}>
                <BrandInfo
                  title={brandInfo.title}
                  description={brandInfo.description}
                  icon={brandInfo.logo}
                />
              </Box>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Box
                  sx={{
                    width: ["90%", null, "45%"],
                    color: "main",
                    font: "base"
                  }}
                ></Box>
              </Box>
            </Container>
          </SectionWithConfigurableHeader>
        )}

        {collectionInfo && !externalContentTimeout && (
          <SectionWithConfigurableHeader title={collectionInfo.title}>
            <Container>
              <Grid cols={[1, null, 3]} sx={{ gridRowGap: "s11", mt: "s11" }}>
                {collectionInfo.banners?.map((banner, index) => (
                  <CardWithLabel
                    name={banner.title}
                    image={banner.image}
                    infoOnly={true}
                    key={index}
                  />
                ))}
              </Grid>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Box
                  as={"p"}
                  sx={{
                    width: ["90%", null, "45%"],
                    color: "main",
                    font: "base",
                    py: "s12",
                    a: {
                      color: "main"
                    }
                  }}
                >
                  {collectionInfo?.text?.length > 0
                    ? RichText.render(collectionInfo.text)
                    : collectionInfo.description}
                </Box>
              </Box>
            </Container>
          </SectionWithConfigurableHeader>
        )}
      </PageContainer>
    </>
  );
};

Page.getInitialProps = async props => {
  const sku = props.query.productPage;
  const productUrl = isNaN(sku) ? sku : null;
  const { language, isProductPageHit } = props;
  if (process.browser) {
    return {
      isProductPageHit,
      sku
    };
  }

  const cacheFilePath = `${language.language}/prod/`;
  const {
    cacheFileExist,
    filenameCategoryCache,
    cachedRequest
  } = await readCacheFile({
    cacheFilePath,
    contextRequest: props.req,
    requestPath: sku
  });
  if (cacheFileExist && !isProductPageHit) {
    return cachedRequest;
  }

  const product = await productFetcher(
    sku,
    null,
    language,
    props.isProductPageHit
  );

  if (isProductPageHit) {
    product.otherVariants = [];
  }

  if (
    product.product === null ||
    (product.product &&
      Object.keys(product?.product).length === 0 &&
      product.product.constructor === Object)
  ) {
    return {
      error: {
        statusCode: 404
      }
    };
  }

  const upSellSkus = getSkusFromProductLinks(product?.product?.relatedProducts);
  const categoryId =
    product?.product?.category.id !== false &&
    product?.product?.category.id !== 2 &&
    product?.product?.category.id;

  let promises = [
    productsBySkusFetcher({
      skus: upSellSkus,
      pageSize: 10,
      language
    }),
    productsFromCollectionFetcher(product.product?.collection.id, 12, language),
    prismicBrandInfoFetcher(product.product?.producerId, language),
    prismicCollectionInfoFetcher(product.product?.collection.id, language)
  ];

  if (categoryId) {
    promises.push(categoryFetcher(categoryId, language).then(res => res));
  }

  const [
    productsCompleteTheLook,
    productsFromCollection,
    brandInfo,
    collectionInfo,
    category
  ] = await Promise.all(promises);

  const result = {
    sku,
    productUrl,
    language,
    product,
    initialCategory: category,
    upSellSkus,
    productsFromCollection,
    productsCompleteTheLook,
    brandInfo,
    collectionInfo
  };

  if (cacheFileExist === false) {
    await saveCacheFile({ cacheFilePath, filenameCategoryCache, result });
  }

  return result;
};

export default Page;
